import { useState } from 'preact/hooks';
import BadgeCelebrationModal from './BadgeCelebrationModal';
import ConfirmationModal from './ConfirmationModal';
import DatePickerModal from './DatePickerModal';
import WorkoutCompletionModal from './WorkoutCompletionModal';
import { formatLocalDate } from '../utils/date';
import { completeWorkout } from '../lib/habitTrackerHelpers';
import { useQueryLocation } from '../hooks';

function CompletionModal({ onClose, videoId }) {
    const [, navigate] = useQueryLocation();
    const [isCompleting, setIsCompleting] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [newBadges, setNewBadges] = useState(null);
    const [completedDate, setCompletedDate] = useState(null);

    const handleCompleteWorkout = async (dateStr) => {
        setIsCompleting(true);
        
        try {
            // Save workout completion to backend via Admin API
            const result = await completeWorkout(dateStr, videoId);
            
            if (result.success) {
                // Store the completed date for later use
                setCompletedDate(dateStr);
                
                // Store in sessionStorage so Calendar page can show success banner
                sessionStorage.setItem('completedWorkoutDate', dateStr);
                
                // Check if any new badges were awarded
                if (result.newBadges && result.newBadges.length > 0) {
                    setNewBadges(result.newBadges);
                    setIsCompleting(false);
                } else {
                    // Navigate to calendar - it will show success banner from sessionStorage
                    navigate('/calendar');
                    onClose();
                }
            } else {
                alert(`Failed to complete workout: ${result.error || 'Please try again.'}`);
                setIsCompleting(false);
            }
        } catch (error) {
            console.error('Error completing workout:', error);
            alert('Failed to complete workout. Please try again.');
            setIsCompleting(false);
        }
    };

    const handleCompleteToday = () => {
        const today = new Date();
        // Check if today is Sunday (rest day)
        if (today.getDay() === 0) {
            alert('Sundays are rest days! Please select a different day.');
            setShowCalendar(true);
            return;
        }
        const dateStr = formatLocalDate(today);
        handleCompleteWorkout(dateStr);
    };

    const handleSelectDifferentDay = () => {
        setShowCalendar(true);
    };

    const handleDateSelect = (date) => {
        setSelectedDate(date);
    };

    const handleContinue = () => {
        if (selectedDate) {
            setShowCalendar(false);
            setShowConfirmation(true);
        }
    };

    const handleConfirmYes = () => {
        handleCompleteWorkout(selectedDate);
    };

    const handleConfirmNo = () => {
        setShowConfirmation(false);
        setShowCalendar(true);
    };

    // Show badge celebration modal if badges were earned
    if (newBadges && newBadges.length > 0 && Array.isArray(newBadges)) {
        // Ensure each badge has required properties
        const validBadges = newBadges.filter(badge => badge && badge.handle && badge.name);
        if (validBadges.length > 0) {
            return <BadgeCelebrationModal 
                badges={validBadges} 
                completedDate={completedDate}
            />;
        }
    }

    if (showConfirmation && selectedDate) {
        return <ConfirmationModal 
            date={selectedDate}
            onConfirm={handleConfirmYes}
            onCancel={handleConfirmNo}
            isCompleting={isCompleting}
        />;
    }

    if (showCalendar) {
        return (
            <DatePickerModal
                onClose={onClose}
                onContinue={handleContinue}
                onBack={() => setShowCalendar(false)}
                selectedDate={selectedDate}
                onDateChange={handleDateSelect}
                isCompleting={isCompleting}
            />
        );
    }

    return (
        <WorkoutCompletionModal
            onClose={onClose}
            onCompleteToday={handleCompleteToday}
            onSelectDifferentDay={handleSelectDifferentDay}
            isCompleting={isCompleting}
        />
    );
}

export default CompletionModal;
